import styles from "./styles.module.scss";
import CloseIcon from "assets/icons/close.svg?react";
import { FC, Props } from "./typings";
import pluginService from "api/pluginService";
import Input from "components/Input";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import Button from "components/Button";
import { getKeys } from "utils/getKeys";
import { generateSingleBinary } from "utils/GenerateSingleBinary";
import {
  CompanyKeyMissingError,
  encryptForCompany,
} from "utils/encryptForCompany";
import editorService, { EditorDocumentType } from "api/editorService";
import ContractCruMetricModal from "components/ContractCruMetricModal";
import {
  IContractCruMetric,
  emptyCruMetric,
} from "dto/Contracts/IContractCru";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileContract } from "@fortawesome/free-solid-svg-icons";
import casesService, { INewTag } from "api/casesService";
import { IKey } from "dto/IKey";
import { ITag, TagType } from "dto/ITag";
import TagBadge from "components/TagBadge";
import ModalsManagerContext, {
  LetterModalOptions,
} from "contexts/ModalsManager";
import { ITemplate } from "dto/ITemplate";
import externalService from "api/externalService";
import { ICaseSimple } from "dto/Cases/ICaseSimple";
import { useAuth } from "react-oidc-context";
import { getUserId } from "utils/getUserId";
import LettersContext from "contexts/LettersContext";
import ModalLayout, { DockPosition } from "components/ModalLayout";
import SearchCasesNew from "components/SearchCasesNew";
import SelectTemplate from "components/SelectTemplate";
import WordIcon from "assets/icons/doc.svg?react";
import ExcelIcon from "assets/icons/xls.svg?react";
import OtherIcon from "assets/icons/other.svg?react";
import PowerIcon from "assets/icons/ppt.svg?react";
import DocsIcon from "assets/icons/filters/docs.svg?react";
import { ItemKind } from "dto/IKindItems";
import { IDossierItem } from "dto/Dossier/IDossierItem";
import SearchLabel from "components/SearchLabel";
import DropdownMui from "components/DropdownMui";
import usePlugin from "hooks/usePlugin";
import KeysContext from "contexts/KeysContext";
import { Can } from "contexts/AbilityContext";
import { ENotificationType, notification } from "utils/notification";
import { useTranslation } from "react-i18next";

const CreateLetterModal: FC<Props> = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const userId = getUserId(auth.user!);

  const { modals, setModals, options } = useContext(ModalsManagerContext);
  const plugin = usePlugin();
  pluginService.useGetVersion(false);

  const [c, setCase] = useState<ICaseSimple | null>(null);
  const { data: keysData } = casesService.useGetKeys({
    id: c?.Id ?? null,
  });

  const { mutate: createDocument } = editorService.useCreateDocument();
  const { mutate: createExternalDocument } =
    externalService.useGetExternalEditor();
  const { mutate: openEditor } = pluginService.useOpenEditor();

  const refWrapper = useRef(null);

  const [title, setTitle] = useState("");
  const [template, setTemplate] = useState<ITemplate | IDossierItem | null>(
    null,
  );

  const caseKeys: IKey = useMemo(
    () => (keysData?.data?.[0] as IKey) ?? [],
    [keysData],
  );

  const [tags, setTags] = useState<ITag[]>([]);

  const { letters, setLetters } = useContext(LettersContext);
  const { keys, setKeys } = useContext(KeysContext);

  const [, setShowCase] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const link = window.location.href;
  const folderId = link.split("dossier/f/")?.[1]?.split("/")?.[0] ?? undefined;

  const [selectedFormat, setSelectedFormat] = useState<string | null>(
    (options as LetterModalOptions)?.format ?? "docx",
  );

  // Rodzaj pisma: zwykły dokument albo umowa (port „Umowa" z NewDocument Magistratu).
  // Umowa dostaje metrykę CRU — można ją wypełnić od razu (walidacja odroczona) albo
  // uzupełnić później w rejestrze umów.
  const [docType, setDocType] = useState<EditorDocumentType>(
    EditorDocumentType.None,
  );
  const [cruMetric, setCruMetric] = useState<IContractCruMetric | null>(null);
  const [showCruMetric, setShowCruMetric] = useState(false);

  const docTypes = [
    { name: "Dokument", value: EditorDocumentType.None },
    { name: "Umowa", value: EditorDocumentType.Contract },
  ];

  const addNewTag = (tag: ITag | null) => {
    const isTag = tags.find((etag) => etag.Name === tag!.Name);
    if (isTag) return;
    setTags([...tags, tag!]);
  };

  const handleRemoveTag = (tag: ITag) =>
    setTags(tags.filter((etag) => tag.Name !== etag.Name));

  const formats = [
    {
      name: "Word",
      value: "docx",
      Icon: WordIcon,
    },
    {
      name: "Excel",
      value: "xlsx",
      Icon: ExcelIcon,
    },
    {
      name: "Power point",
      value: "pptx",
      Icon: PowerIcon,
    },
    {
      name: "Google Docs",
      value: "googledoc",
      Icon: DocsIcon,
    },
    {
      name: "Inny",
      value: "other",
      Icon: OtherIcon,
    },
  ];

  const handleClick = async () => {
    setIsLoading(true);
    const newTags: INewTag[] = tags
      .filter((tag) => tag.Id === "-1")
      .map((tag) => ({
        Id: null,
        Name: tag.Name,
        Type: tag.Type,
      }));

    const tagIds = tags.filter((tag) => tag.Id !== "-1").map((tag) => tag.Id);

    if (selectedFormat === "googledoc") {
      createExternalDocument(
        {
          CaseId: c ? c.Id : null,
          NewTags: newTags,
          TagIds: tagIds,
          TemplateId: template
            ? (template as IDossierItem).DossierId ?? template.Id
            : null,
          Title: title,
        },
        {
          onSuccess: (response) => {
            if (options as LetterModalOptions) {
              (options as LetterModalOptions)?.callback?.();
            }
            setIsLoading(false);
            window.open(response.Uri);
            handleClose();

            //@ts-ignore
            setLetters([...letters, response.data.Items[0]]);
          },
          onError: () => setIsLoading(false),
        },
      );
      return;
    }

    const myKeys = await getKeys(userId);

    let objectKeys = null;

    if (!c) {
      // Dokument bez sprawy dostaje klucz BEZ rodzica (BE zapisuje UserKeyId =
      // dbCase?.InitialEncryptionKeyId, czyli null). Klucz tego dokumentu owija klucz
      // publiczny FIRMY (docs/model-kluczy-firmowych.md, inwariant I1) — wcześniej był to
      // klucz AKTYWNEGO PROFILU, przez co dokument bez sprawy widział wyłącznie jego autor.
      // Wtyczka dostaje klucz TEGO dokumentu przy otwarciu (rozstrzygnięcie 17), a nie
      // klucz kontekstu, więc podmiana owijki jej nie dotyczy.
      try {
        objectKeys = await encryptForCompany();
      } catch (error) {
        // FAIL-CLOSED — patrz utils/encryptForCompany.ts.
        setIsLoading(false);
        notification({
          title: t("errors.occurred"),
          text:
            error instanceof CompanyKeyMissingError
              ? error.message
              : t("errors.somethingWentWrong"),
          type: ENotificationType.ERROR,
        });
        return;
      }
    }

    if (c && caseKeys.PublicKey) {
      objectKeys = await generateSingleBinary(caseKeys.PublicKey);
    }

    if (!objectKeys) return;

    createDocument(
      {
        CaseId: c ? c.Id : null,
        NewTags: newTags,
        PrivateKey: objectKeys!.PrivateKey,
        PublicKey: objectKeys!.PublicKey,
        TagIds: tagIds,
        TemplateId: template
          ? (template as IDossierItem).DossierId ?? template.Id
          : null,
        Title: title,
        Format: selectedFormat!,
        FolderId: folderId,
        Type: docType,
        ContractCruMetric:
          docType === EditorDocumentType.Contract
            ? cruMetric ?? { ...emptyCruMetric(title), Subject: title }
            : null,
      },
      {
        onSuccess: async (response) => {
          //if (!response.data.IsConnected) {
          await plugin.connect();
          //}

          setIsLoading(false);

          if (options as LetterModalOptions) {
            (options as LetterModalOptions)?.callback?.();
          }

          setKeys([
            ...keys,
            {
              Id: response.data.KeyId,
              PrivateKey: objectKeys!.PrivateKey,
              ParentKeyId: caseKeys?.Id,
              Armored: false,
              Initial: false,
              PublicKey: objectKeys!.PublicKey,
            },
          ]);

          openEditor({ Id: response.data.Id, Kind: ItemKind.EditorDocument });
          // addVersion({
          //   Id: response.data.Id,
          //   Name: title,
          // });
          handleClose();
          //@ts-ignore
          //setLetters([response.data.Items[0], ...letters]);
        },
        onError: () => setIsLoading(false),
      },
    );
  };

  const handleClose = () => setModals({ ...modals, hide: true });

  // if (isFetching) {
  //   return (
  //     <div className={styles.modal}>
  //       <Loading />
  //     </div>
  //   );
  // }

  useEffect(() => {
    if (template && !title?.length) {
      if (template as IDossierItem) {
        setTitle(
          (template as IDossierItem).Label ??
            (template as IDossierItem).FileName,
        );
      }
    }
  }, [template]);

  // if (isError)
  //   return (
  //     <PluginNotExist
  //       message="Aby tworzyć dokumenty w Nocie konieczna jest instalacja odpowiedniej wtyczki. Umożliwi ona prawidłową komunikację i synchronizację danych."
  //       handleClose={() => setModals({ ...modals, createLetter: false })}
  //     />
  //   );

  return (
    <ModalLayout
      name="createLetter"
      dock={DockPosition.DISABLED}
      off={true}
      customX={8}
      customY={0}
      ignore
      isAnimation
      refWrapper={refWrapper}
      fullHeight={true}
    >
      <div className={styles.wrapper}>
        <div className={styles.title}>
          {t("documents.newDocument")}
          <CloseIcon onClick={handleClose} className={styles.closeIcon} />
        </div>
        <div className={styles.content}>
          <div className={styles.inputs}>
            {/* {isWord ? (
                <SearchTemplates
                  onChange={(t) => {
                    setTemplate(t);
                    if (!title.length) {
                      setTitle(t.S1);
                    }
                  }}
                />
              ) : (
                <SearchExternalTemplate
                  onChange={(t) => {
                    setTemplate(t);
                    if (!title.length) {
                      setTitle(t.S1);
                    }
                  }}
                />
              )} */}
            <Can I="accessModule" a="cases">
              <SearchCasesNew
                settedCase={c}
                setAddNewEntity={() => {
                  setShowCase(true);
                }}
                onChoose={(c) => setCase(c)}
              />
            </Can>
            <Input
              label={t("forms.title")}
              value={title}
              placeholder={t("documents.titlePlaceholder")}
              onChange={(t) => setTitle(t)}
              onChooseTag={addNewTag}
              tags
            />

            <SelectTemplate
              isExternal={false}
              selectedTemplate={template}
              onChoose={(t) => {
                setTemplate(t);
              }}
            />

            <DropdownMui
              list={formats}
              defaultValue={selectedFormat}
              label={t("documents.documentFormat")}
              onChange={(f) => {
                setSelectedFormat(f.value);
              }}
            />

            {selectedFormat !== "googledoc" && (
              <DropdownMui
                list={docTypes}
                defaultValue={docType}
                label="Rodzaj pisma"
                onChange={(d) => setDocType(d.value)}
              />
            )}

            {docType === EditorDocumentType.Contract && (
              <div className={styles.cruRow}>
                <button
                  type="button"
                  className={styles.cruBtn}
                  onClick={() => setShowCruMetric(true)}
                >
                  <FontAwesomeIcon icon={faFileContract} />
                  {cruMetric ? "Metryka CRU — edytuj" : "Metryka CRU — wypełnij"}
                </button>
                <span className={styles.cruHint}>
                  {cruMetric
                    ? "Metryka zapisana z pismem (uzupełnisz ją w rejestrze umów)."
                    : "Umowa trafi do rejestru umów; metrykę można uzupełnić później."}
                </span>
              </div>
            )}

            <div className={styles.tags}>
              <div className={styles.row1}>
                <SearchLabel
                  label={t("common.label")}
                  type={TagType.EditorDocuments}
                  onChoose={addNewTag}
                />
              </div>
              <div className={styles.row2}>
                {!!tags.length && (
                  <>
                    {tags.map((tag) => (
                      <TagBadge
                        useBigChip
                        key={tag.Id}
                        tag={tag}
                        onRemove={handleRemoveTag}
                      />
                    ))}
                  </>
                )}
              </div>
            </div>

            {/* <SearchCases
                settedCase={c}
                removeFocus
                setAddNewEntity={() => {
                  setShowCase(true);
                }}
                onChoose={(c) => setCase(c)}
              /> */}
          </div>
        </div>
        <div className={styles.footer}>
          <Button
            disabled={!title.length}
            onClick={handleClick}
            text={t("common.create")}
            loading={isLoading}
          />
        </div>
      </div>
      {/* {showCase && (
          <AdditionalClientModal
            showCase={showCase}
            onCreateCase={(c) => {
              setCase(c);
              setShowCase(false);
            }}
          />
        )} */}

      {showCruMetric && (
        <ContractCruMetricModal
          title="Metryka umowy (CRU)"
          subtitle={title || undefined}
          initial={cruMetric ?? { ...emptyCruMetric(title), Subject: title }}
          deferValidation
          saveLabel="Zapisz metrykę"
          onSave={(m) => {
            setCruMetric(m);
            setShowCruMetric(false);
          }}
          onClose={() => setShowCruMetric(false)}
        />
      )}
    </ModalLayout>
  );
};

export default CreateLetterModal;
